import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { studio } from "./api";

/**
 * Broadcasting from an encoder that is not this browser.
 *
 * ## Why this is not the studio
 *
 * The studio composites a picture and publishes it. Here the host's own
 * software does all of that, and the console's whole job is to hand over an
 * address and then get out of the way - so there is no camera, no layers and no
 * mix. What is left is the two things somebody setting up OBS actually needs:
 * where to point it, and whether it worked.
 *
 * ## Listeners cannot tell the difference, and that is the design
 *
 * OBS over RTMPS and the browser studio over WHIP arrive at the same Cloudflare
 * live input and leave by the same playback URL. Nothing downstream knows which
 * was used, so nothing downstream had to change to support this - the room is
 * live carrying a stream either way.
 *
 * ## The preview is muted, always
 *
 * The host is monitoring in their own software, on the same machine, seconds
 * ahead of this. Unmuting would put their own audio back into the room they are
 * speaking in, which is feedback - so this has no volume control at all rather
 * than one that is a bad idea to touch.
 */
export function ExternalColumn() {
  /*
   * Fetched once here rather than in each half.
   *
   * The preview needs the playback URL and the fields below need the address,
   * and they come from the same call - which returns a stream key, so making it
   * twice would put a broadcast credential on the wire twice for one screen.
   */
  const credentials = useQuery({
    queryKey: ["studio-credentials"],
    queryFn: studio.credentials,
    // Write access to somebody's broadcast should not sit in a cache waiting
    // for a shoulder to look over.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const status = useQuery({
    queryKey: ["ingest-status"],
    queryFn: studio.status,
    /*
     * Fast while waiting, slow once connected.
     *
     * The interesting moment is the one before an encoder appears - a host
     * checking whether they pasted the key correctly wants an answer in
     * seconds. Afterwards this is only watching for a drop, and each poll costs
     * a Cloudflare API call.
     */
    refetchInterval: (query) => (query.state.data?.connected ? 15_000 : 5_000),
    refetchIntervalInBackground: false,
  });

  const connected = status.data?.connected ?? false;

  return (
    <aside
      className="flex min-w-0 flex-col gap-6 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
      data-external
    >
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">External encoder</span>
        <span
          className={`font-mono text-[11px] ${
            connected ? "text-fresh" : "text-ghost"
          }`}
          data-external-state={connected ? "connected" : "waiting"}
        >
          {connected ? "receiving" : "waiting"}
        </span>
      </div>

      <Preview hls={credentials.data?.hls ?? null} connected={connected} />

      {/*
        The status line says which protocol arrived, because that is the one
        piece of evidence separating "your OBS connected" from "the browser
        studio is still publishing in another tab" - two situations that look
        identical from here and want opposite fixes.
      */}
      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {connected
          ? `receiving over ${status.data?.protocol ?? "an encoder"} — press Go on air when you are ready`
          : "point your encoder at the address below, then start streaming in it"}
      </p>

      <Credentials
        rtmps={credentials.data?.rtmps ?? null}
        pending={credentials.isPending}
      />
    </aside>
  );
}

/**
 * What is actually arriving, or a placeholder.
 *
 * ## HLS, not WebRTC, and not by preference
 *
 * This mode is RTMP by definition, and Cloudflare serves no realtime playback
 * for an RTMP broadcast - `webRTC/play` answers `409 Live broadcast not started
 * yet` however long you wait, because that endpoint exists only for WHIP
 * publishers. An RTMP broadcast is watched over HLS or not at all.
 *
 * This previewed over WebRTC at first and sat on "waiting for the first frames"
 * for ever, while the listener's own player - which falls back - showed the
 * broadcast perfectly. The host's console was the last place still getting it
 * wrong.
 *
 * ## It only subscribes once something is publishing
 *
 * The manifest 500s when the input is idle, so an early attempt is a request
 * that cannot succeed, retried for as long as the host takes to configure OBS.
 * The status poll already knows; this waits for it.
 */
function Preview({
  hls,
  connected,
}: {
  hls: string | null;
  connected: boolean;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const element = video.current;

    if (!element || !hls || !connected) return;

    let live = true;
    let engine: { destroy: () => void } | null = null;
    let timer: number | undefined;

    const attach = async () => {
      const Hls = (await import("hls.js")).default;

      if (!live) return;

      if (!Hls.isSupported()) {
        // Safari, which plays HLS natively and has no MSE for hls.js to use.
        element.src = hls;

        return;
      }

      const instance = new Hls({ enableWorker: true, backBufferLength: 30 });

      instance.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !live) return;

        /*
         * Retried rather than reported. Cloudflare marks an encoder connected
         * before the first keyframe is playable, so the manifest legitimately
         * 500s for a few seconds at the start of every broadcast - and telling
         * a host their stream failed while it is in the middle of starting is
         * worse than saying nothing.
         */
        instance.destroy();
        engine = null;
        timer = window.setTimeout(() => void attach(), 2000);
      });

      instance.loadSource(hls);
      instance.attachMedia(element);

      engine = instance;
    };

    void attach();

    return () => {
      live = false;
      clearTimeout(timer);
      engine?.destroy();
      element.removeAttribute("src");
    };
  }, [hls, connected]);

  return (
    <div className="relative overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black">
      <video
        ref={video}
        // Muted with no way to change it: the host is monitoring this in their
        // own software already, and unmuting would be feedback.
        muted
        playsInline
        autoPlay
        className="block aspect-video w-full"
        data-external-preview
        onPlaying={() => setPlaying(true)}
        onEmptied={() => setPlaying(false)}
      />

      {!playing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <span className="meta-caps text-ghost">
            {connected ? "starting…" : "nothing arriving yet"}
          </span>
          <span className="font-mono text-[10px] text-ghost">
            {connected
              ? "waiting for the first frames"
              : "this fills in on its own once your encoder connects"}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * The address, and the key that goes with it.
 *
 * ## The key is masked until asked for
 *
 * Anyone holding it can broadcast as this station, and a host setting up OBS is
 * quite likely to be screen-sharing while they do it - that is how a stream key
 * ends up in a tutorial video. Hidden by default costs one click and removes
 * the most likely way this leaks.
 */
function Credentials({
  rtmps,
  pending,
}: {
  rtmps: { url: string; key: string } | null;
  pending: boolean;
}) {
  const [shown, setShown] = useState(false);

  if (pending) {
    return (
      <p className="font-mono text-[10px] text-ghost">fetching your address…</p>
    );
  }

  if (!rtmps) {
    return (
      <p className="font-mono text-[10px] text-hot-text">
        could not fetch your streaming address — reload to try again
      </p>
    );
  }

  const { url, key } = rtmps;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Server" value={url} hook="url" />

      <Field
        label="Stream key"
        value={key}
        hook="key"
        secret
        revealed={shown}
        onReveal={() => setShown((on) => !on)}
      />

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        in OBS: Settings → Stream → Custom, then paste these two. anyone with the
        key can broadcast as you, so treat it like a password.
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  hook,
  secret = false,
  revealed = true,
  onReveal,
}: {
  label: string;
  value: string;
  hook: string;
  secret?: boolean;
  revealed?: boolean;
  onReveal?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused - an insecure origin, or permission denied. The
      // value is on screen and selectable, so there is still a way through.
    }
  };

  return (
    <label className="flex flex-col gap-1">
      <span className="meta-caps text-faint">{label}</span>

      <span className="flex items-stretch gap-1.5">
        <input
          readOnly
          value={secret && !revealed ? "•".repeat(24) : value}
          onFocus={(event) => event.target.select()}
          data-external-field={hook}
          className="min-w-0 flex-1 truncate rounded-[var(--radius-control)] border border-line bg-deep px-2.5 py-2 font-mono text-[11px] text-soft"
        />

        {secret && (
          <button
            type="button"
            onClick={onReveal}
            data-external-reveal
            className="shrink-0 rounded-[var(--radius-control)] border border-line px-2.5 font-mono text-[10px] text-soft transition-colors hover:text-ink"
          >
            {revealed ? "hide" : "show"}
          </button>
        )}

        <button
          type="button"
          onClick={() => void copy()}
          data-external-copy={hook}
          className="shrink-0 rounded-[var(--radius-control)] border border-line px-2.5 font-mono text-[10px] text-soft transition-colors hover:text-ink"
        >
          {copied ? "copied" : "copy"}
        </button>
      </span>
    </label>
  );
}
